import React, { useEffect, useRef, useState } from "react";
import {
    answerSelected,
    firstTryCount,
    GRID,
    isDone,
    PictureOutcome,
    PictureState,
    selectTile,
    startBoard,
    Tile,
    tilesLeft,
} from "./pictureGame.ts";
import { PictureId } from "./pictureArt.ts";
import { PictureArt } from "./PictureArt.tsx";
import { cardFaces } from "../cardFaces.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

export interface PictureResult {
    picture: PictureId;
    /** Every tile as it ended: lifted, with the wrong tries it took. */
    tiles: Tile[];
}

interface PictureGameRunProps {
    /** The 25 facts, already drawn: the page builds them when Start is pressed (ADR 0133 §3). */
    board: Tile[];
    picture: PictureId;
    onFinish(result: PictureResult): void;
}

interface Feedback {
    outcome: Exclude<PictureOutcome, "skipped">;
    /** What the child reads. */
    text: string;
    /** What a screen reader is told: the same, and how many tiles are left. */
    announcement: string;
}

/**
 * One board of Hidden Picture (ADR 0133 §1, §5): the picture as one SVG, a 5 x 5 grid
 * of tile buttons over it, and under it the picked fact with its answer box.
 *
 * The engine (`pictureGame.ts`) is pure and holds no clock, so the whole game is
 * answers: state is a plain `useState` and nothing here draws a random fact. A lifted
 * tile is a transparent div, no longer a button. Focus stays in the answer box after
 * every answer and after a tile is picked, so a keyboard player never leaves it.
 */
export function PictureGameRun({ board, picture, onFinish }: PictureGameRunProps) {
    const t = useTranslation();
    const [state, setState] = useState<PictureState>(() => startBoard(board));
    const [typed, setTyped] = useState("");
    const [feedback, setFeedback] = useState<Feedback | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const reported = useRef(false);

    const done = isDone(state);
    const picked = state.selected !== null ? state.tiles[state.selected] : null;
    const faces = picked ? cardFaces(picked.problem) : null;

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        if (done && !reported.current) {
            reported.current = true;
            onFinish({ picture, tiles: state.tiles });
        }
    }, [done, picture, state.tiles, onFinish]);

    function check() {
        const answered = picked;
        const { state: next, outcome } = answerSelected(state, typed);
        if (outcome === "skipped" || !answered) {
            return;
        }
        const left = tilesLeft(next);
        const answeredFaces = cardFaces(answered.problem);
        const shown = format(t("flashCards.pictureAnswerShownTemplate"), {
            fact: answeredFaces.front,
            answer: answeredFaces.back,
        });
        let text: string;
        let announcement: string;
        if (outcome === "correct") {
            text = t("flashCards.pictureRight");
            announcement = format(t("flashCards.pictureAnnounceRightTemplate"), { n: left });
        } else if (outcome === "wrong") {
            text = t("flashCards.pictureTryAgain");
            // With the fact, so a second wrong answer on another tile is heard again.
            announcement = format(t("flashCards.pictureAnnounceWrongTemplate"), { fact: answeredFaces.front });
        } else {
            text = shown;
            announcement = format(t("flashCards.pictureAnnounceMissedTemplate"), {
                fact: answeredFaces.front,
                answer: answeredFaces.back,
                n: left,
            });
        }
        // Said last, so the next fact is the last thing heard (§5).
        if (next.selected !== null && next.selected !== state.selected) {
            announcement +=
                " " +
                format(t("flashCards.pictureAnnounceNextTemplate"), {
                    fact: cardFaces(next.tiles[next.selected].problem).front,
                });
        } else if (next.selected === null) {
            announcement += " " + t("flashCards.pictureAnnounceDone");
        }
        setState(next);
        setTyped("");
        setFeedback({ outcome, text, announcement });
        inputRef.current?.focus();
    }

    function pick(index: number) {
        const next = selectTile(state, index);
        if (next !== state) {
            setState(next);
            setTyped("");
        }
        inputRef.current?.focus();
    }

    const verdictClass = feedback ? (feedback.outcome === "correct" ? "correct" : "wrong") : null;
    const left = tilesLeft(state);

    return (
        <div className="quizRun pictureRun">
            <div className="quizStatus pictureStatus">
                <span className="quizScore">{format(t("flashCards.pictureLeftTemplate"), { n: left })}</span>
                <span className="pictureFirstTry">
                    {format(t("flashCards.pictureRightTemplate"), { n: firstTryCount(state.tiles) })}
                </span>
            </div>

            <div className="pictureBoard">
                <PictureArt id={picture} />
                <div className="pictureGrid" role="group" aria-label={t("flashCards.pictureBoardLabel")}>
                    {state.tiles.map((tile, index) => {
                        if (tile.lifted) {
                            return <div key={index} className="pictureTile pictureTile--lifted" />;
                        }
                        const row = Math.floor(index / GRID) + 1;
                        const col = (index % GRID) + 1;
                        const front = cardFaces(tile.problem).front;
                        const selected = index === state.selected;
                        return (
                            <button
                                key={index}
                                type="button"
                                className={`pictureTile${selected ? " pictureTile--selected" : ""}`}
                                aria-label={format(t("flashCards.pictureTileAriaTemplate"), { row, col, fact: front })}
                                aria-current={selected ? "true" : undefined}
                                onClick={() => pick(index)}
                            >
                                {front}
                            </button>
                        );
                    })}
                </div>
            </div>

            <form
                className="quizCard pictureCard"
                onSubmit={(e) => {
                    e.preventDefault();
                    check();
                }}
            >
                <p className="pictureProblem">{faces ? `${faces.front} =` : " "}</p>
                <div className="pictureAnswerRow">
                    <input
                        ref={inputRef}
                        className={`quizInput pictureInput${verdictClass ? ` quizInput--${verdictClass}` : ""}`}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value.replace(/[^\d]/g, ""))}
                        onKeyDown={(e) => {
                            // As on the quiz: implicit submission on Enter does not always fire.
                            if (e.key === "Enter") {
                                e.preventDefault();
                                check();
                            }
                        }}
                        disabled={done}
                        aria-label={faces ? format(t("flashCards.answerForAriaTemplate"), { front: faces.front }) : undefined}
                    />
                    <button type="submit" className="btn btn-primary pictureCheck" disabled={done}>
                        {t("flashCards.pictureCheck")}
                    </button>
                </div>
            </form>

            {/* Always rendered, always the same height, as on the quiz (ADR 0067). */}
            <p className={`quizVerdict pictureFeedback${verdictClass ? ` quizVerdict--${verdictClass}` : ""}`}>
                {feedback?.text}
            </p>

            <p className="pictureAnnouncer" role="status" aria-live="polite">
                {feedback?.announcement}
            </p>

            <p className="quizHint">{t("flashCards.pictureHint")}</p>
        </div>
    );
}

export default PictureGameRun;
